<template>
  <div class="row">
    <div class="col-12">
      <div class="card pg-card mb-25 border-0 letter-spacing">
        <div
          class="card-head d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25"
        >
          <div class="d-sm-flex align-items-center">
            <button
              class="pg-btn-primary position-relative transition border-0 fw-medium text-white d-inline-flex align-items-center me-10 mb-10 mb-lg-0"
              type="button"
              @click="openCreate"
              v-if="auth.can('SETTINGS')"
            >
              <i class="flaticon-plus position-relative fs-12 me-8"></i>
              Nouveau pack
            </button>
          </div>
          <div class="pg-search position-relative">
            <i class="flaticon-search pg-search-icon"></i>
            <input
              type="text"
              class="form-control ps-40"
              v-model="searchTerm"
              placeholder="Rechercher un pack..."
            />
          </div>
        </div>
        <div class="card-body p-0">
          <div class="table-scroll-outer">
            <div class="table-scroll-mobile">
              <table class="table pg-table text-nowrap align-middle mb-0">
                <thead>
                  <tr>
                    <th v-for="h in headers" :key="h.value">{{ h.text }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loading">
                    <td :colspan="headers.length" class="text-center py-40">
                      <div class="spinner-border text-success" role="status">
                        <span class="visually-hidden">Chargement...</span>
                      </div>
                    </td>
                  </tr>
                  <tr v-else-if="!pagedRows.length">
                    <td :colspan="headers.length" class="text-center py-40 text-muted">
                      <i class="flaticon-money fs-40 d-block mb-10 opacity-25"></i>
                      Aucun pack trouvé
                    </td>
                  </tr>
                  <template v-else>
                    <tr v-for="row in pagedRows" :key="row.id">
                      <td class="fw-medium">{{ row.name }}</td>
                      <td>
                        <span v-if="!row.default_fee && !row.cities_count" class="text-muted">—</span>
                        <span v-else class="status-pill status-pill-success" :title="feesSummary(row)">
                          <template v-if="row.default_fee">Toutes les villes</template>
                          <template v-if="row.default_fee && row.cities_count > 0"> + </template>
                          <template v-if="row.cities_count > 0">
                            {{ row.cities_count }} ville{{ row.cities_count > 1 ? "s" : "" }}
                          </template>
                        </span>
                      </td>
                      <td>
                        <span class="status-pill status-pill-info">
                          {{ row.clients_count }} client{{ row.clients_count > 1 ? "s" : "" }}
                        </span>
                      </td>
                      <td>
                        <div class="switch">
                          <input
                            type="checkbox"
                            class="switch-input"
                            :id="'switch-' + row.id"
                            :checked="row.active"
                            @change="togglePackActive(row.id, $event.target.checked)"
                          />
                          <label class="switch-label" :for="'switch-' + row.id"></label>
                        </div>
                      </td>
                      <td>
                        <div class="d-flex align-items-center gap-8" v-if="auth.can('SETTINGS')">
                          <button
                            type="button"
                            class="pg-icon-btn pg-icon-btn-edit"
                            title="Modifier"
                            @click="editPack(row.id)"
                          >
                            <i class="flaticon-pen"></i>
                          </button>
                          <button
                            type="button"
                            class="pg-icon-btn pg-icon-btn-delete"
                            title="Supprimer"
                            :disabled="row.clients_count > 0"
                            @click="deletePack(row.id)"
                          >
                            <i class="flaticon-delete"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </div>
          <div class="px-15 px-md-20 pb-15 border-top">
            <PaginatorContent :pagination="pager" :offset="3" @paginate="goToPage" />
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="modal fade" id="packModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-xl modal-dialog-scrollable">
      <div class="modal-content pg-modal-content">
        <div class="modal-header pg-modal-header">
          <div class="d-flex align-items-center">
            <span class="pg-modal-icon">
              <i :class="formPack.id ? 'flaticon-pen' : 'flaticon-money'"></i>
            </span>
            <h5 class="modal-title ms-12 mb-0">
              {{ formPack.id ? "Modifier le pack" : "Nouveau pack tarifaire" }}
            </h5>
          </div>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitPack">
            <div class="form-group mb-20">
              <label class="d-block text-black fw-medium mb-10" for="pack-name">Nom du pack</label>
              <input id="pack-name" type="text" v-model="formPack.name" class="form-control" />
            </div>

            <!-- Ajout rapide d'un tarif -->
            <h6 class="fw-bold mb-12">Ajouter des tarifs par ville</h6>
            <div class="row g-10 align-items-end mb-25">
              <div class="col-12">
                <label class="d-block text-black fw-medium mb-8 fs-13" for="pack-fee-city">
                  Villes
                  <span class="text-muted fw-normal">(une ou plusieurs : les mêmes frais seront appliqués à chacune)</span>
                </label>
                <v-select
                  v-model="feeForm.cities"
                  input-id="pack-fee-city"
                  multiple
                  label="name"
                  :options="cityOptions"
                  :reduce="(c) => c.id"
                  :close-on-select="false"
                  placeholder="Rechercher et choisir des villes..."
                >
                  <template #no-options>Aucune ville trouvée.</template>
                </v-select>
                <div class="d-flex gap-10 mt-6 fs-13">
                  <button type="button" class="btn btn-link p-0 fs-13" @click="selectAllCities">
                    Sélectionner toutes les villes
                  </button>
                  <button v-if="feeForm.cities.length" type="button" class="btn btn-link p-0 fs-13 text-muted" @click="feeForm.cities = []">
                    Vider ({{ feeForm.cities.length }})
                  </button>
                </div>
              </div>
              <div class="col-lg-3 col-md-4 col-4" v-for="f in feeFields" :key="f.key">
                <label class="d-block text-black fw-medium mb-8 fs-13" :for="'pack-fee-' + f.key">{{ f.label }}</label>
                <div class="input-group">
                  <input
                    :id="'pack-fee-' + f.key"
                    type="number"
                    min="0"
                    step="0.01"
                    v-model="feeForm[f.key]"
                    class="form-control"
                    placeholder="0"
                  />
                  <span class="input-group-text fs-12">{{ $devis }}</span>
                </div>
              </div>
              <div class="col-lg-3 col-md-12">
                <button
                  type="button"
                  class="pg-btn-primary border-0 transition fw-medium text-white w-100"
                  @click="applyFeeForm"
                >
                  <i class="flaticon-plus fs-11 me-6"></i> Appliquer{{ feeForm.cities.length > 1 ? " à " + feeForm.cities.length + " villes" : "" }}
                </button>
              </div>
            </div>

            <!-- Grille : toutes les villes, paginée côté serveur -->
            <div class="d-flex align-items-center justify-content-between flex-wrap gap-10 mb-8">
              <div class="d-flex align-items-center flex-wrap gap-10">
                <h6 class="fw-bold mb-0">Tarifs par ville</h6>
                <button type="button" class="pg-info-pill" @click="showExample = !showExample">
                  <i class="flaticon-info me-4"></i> Exemple de configuration
                </button>
              </div>
              <div class="pg-search pg-grid-search position-relative">
                <i class="flaticon-search pg-search-icon"></i>
                <input
                  type="search"
                  class="form-control ps-40"
                  v-model="grid.search"
                  placeholder="Rechercher une ville..."
                  aria-label="Rechercher une ville"
                  @input="onGridSearch"
                />
              </div>
            </div>
            <p class="text-muted fs-13 mb-12">
              Toutes les villes sont chargées. Modifiez les frais directement dans le tableau ; une ville
              laissée vide utilise le tarif « Toutes les villes ».
            </p>
            <div v-if="showExample" class="pg-example mb-12">
              Renseignez la ligne <strong>« Toutes les villes »</strong> pour le tarif par défaut
              (ex. 35 / 10 / 10 {{ $devis }}), puis les villes au tarif différent
              (ex. Casablanca 20 / 0 / 0 {{ $devis }}). Une ville sans frais propres utilise le tarif
              « Toutes les villes » ; sans ce tarif, les frais habituels (client / ville) s'appliquent.
            </div>

            <div class="pg-fees-table-wrap" :class="{ 'is-loading': grid.loading }">
              <table class="table pg-fees-table mb-0">
                <thead>
                  <tr>
                    <th>Ville</th>
                    <th v-for="f in feeFields" :key="f.key">{{ f.label }} ({{ $devis }})</th>
                    <th class="text-end"><span class="visually-hidden">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  <!-- Tarif par défaut -->
                  <tr class="pg-default-row" :class="{ 'pg-row-changed': isChanged(defaultRow) }">
                    <td class="fw-medium">
                      Toutes les villes <span class="pg-default-badge ms-6">par défaut</span>
                    </td>
                    <td v-for="f in feeFields" :key="f.key">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        v-model="defaultRow[f.key]"
                        class="form-control form-control-sm pg-inline-input"
                        placeholder="—"
                        :aria-label="`${f.label} — Toutes les villes`"
                        @input="track(defaultRow)"
                      />
                    </td>
                    <td class="text-end">
                      <button
                        v-if="hasValue(defaultRow)"
                        type="button"
                        class="pg-icon-btn pg-icon-btn-delete"
                        title="Vider le tarif par défaut"
                        @click="clearRow(defaultRow)"
                      >
                        <i class="flaticon-delete"></i>
                      </button>
                    </td>
                  </tr>

                  <tr v-if="grid.loading && !grid.rows.length">
                    <td colspan="5" class="text-center py-20">
                      <span class="spinner-border spinner-border-sm text-success" role="status"></span>
                    </td>
                  </tr>
                  <tr v-else-if="!grid.rows.length">
                    <td colspan="5" class="text-center text-muted py-20">
                      {{ grid.search ? "Aucune ville ne correspond à votre recherche." : "Aucune ville active." }}
                    </td>
                  </tr>
                  <tr
                    v-for="row in grid.rows"
                    :key="row.city_id"
                    :class="{ 'pg-row-changed': isChanged(row), 'pg-row-own': hasValue(row) && !isChanged(row) }"
                  >
                    <td class="fw-medium">{{ row.city_name }}</td>
                    <td v-for="f in feeFields" :key="f.key">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        v-model="row[f.key]"
                        class="form-control form-control-sm pg-inline-input"
                        :placeholder="placeholderFor(f.key)"
                        :title="hasValue(row) ? '' : 'Vide : tarif « Toutes les villes »'"
                        :aria-label="`${f.label} — ${row.city_name}`"
                        @input="track(row)"
                      />
                    </td>
                    <td class="text-end">
                      <button
                        v-if="hasValue(row)"
                        type="button"
                        class="pg-icon-btn pg-icon-btn-delete"
                        :title="`Retirer le tarif propre à ${row.city_name}`"
                        @click="clearRow(row)"
                      >
                        <i class="flaticon-delete"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="pg-grid-pager">
              <PaginatorContent
                v-if="grid.meta.total > 0"
                :pagination="grid.meta"
                :offset="2"
                @paginate="loadGrid"
              />
            </div>
          </form>
        </div>
        <div class="modal-footer pg-modal-footer">
          <span v-if="changeCount" class="me-auto fs-13 pg-changes-note">
            {{ changeCount }} modification{{ changeCount > 1 ? "s" : "" }} non enregistrée{{ changeCount > 1 ? "s" : "" }}
          </span>
          <button
            type="button"
            class="pg-btn-outline transition fw-medium"
            data-bs-dismiss="modal"
          >
            Annuler
          </button>
          <button
            type="button"
            @click="submitPack"
            class="pg-btn-primary border-0 transition fw-medium text-white"
            :disabled="saving"
          >
            <span v-if="saving" class="spinner-border spinner-border-sm me-6" role="status"></span>
            Enregistrer le pack
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from "@/stores/auth";
import api from "@/utils/axios";
import { currency } from "@/utils/currency";
import { Modal } from "bootstrap";
import { computed, onMounted, reactive, ref } from "vue";
import { useAppToast } from "@/composables/useAppToast";
import { useClientTable } from "@/composables/useClientTable";

// Valeur du select pour "Toutes les villes" (city_id = null côté API)
const ALL_CITIES = "all";
const FEE_KEYS = ["delivered_fees", "refused_fees", "returned_fees"];
const PER_PAGE = 25;

const isEmptyFee = (v) => v === null || v === "" || v === undefined;
const norm = (v) => (isEmptyFee(v) ? null : Number(v));

export default {
  name: "PacksSettings",
  setup: () => {
    const auth = useAuthStore();
    const { showToast } = useAppToast();
    const themeColor = "#06b48a";
    const searchTerm = ref("");

    const feeFields = [
      { key: "delivered_fees", label: "Frais de livraison" },
      { key: "refused_fees", label: "Frais de refus" },
      { key: "returned_fees", label: "Frais de retour" },
    ];

    const formPack = reactive({ id: null, name: "" });
    const feeForm = reactive({ cities: [], delivered_fees: null, refused_fees: null, returned_fees: null });
    const showExample = ref(false);
    const saving = ref(false);

    const headers = [
      { text: "Nom", value: "name" },
      { text: "Tarifs", value: "city_fees" },
      { text: "Clients", value: "clients_count" },
      { text: "Statut", value: "active" },
      { text: "Actions", value: "actions" },
    ];

    const packs = ref([]);
    const cities = ref([]);
    const loading = ref(true);
    const { pagedRows, pager, goToPage } = useClientTable(packs, searchTerm);

    onMounted(async () => {
      fetchPacks();
      fetchCities();
    });

    async function fetchPacks() {
      loading.value = true;
      try {
        const response = await api.get("/api/auth/pack_list");
        if (response && response.data) {
          packs.value = response.data;
        }
      } catch (error) {
        console.error("Error fetching packs:", error);
      } finally {
        loading.value = false;
      }
    }

    // Liste légère (id, nom) pour le select d'ajout rapide
    async function fetchCities() {
      try {
        const response = await api.get("/api/auth/citiesList");
        if (response && response.data) {
          cities.value = response.data
            .map((c) => ({ id: c.id, name: String(c.name || "").trim() }))
            .sort((a, b) => a.name.localeCompare(b.name, "fr"));
        }
      } catch (error) {
        console.error("Error fetching cities:", error);
      }
    }

    // ---------- Helpers ----------
    function formatFee(value) {
      return !isEmptyFee(value) ? `${value} ${currency()}` : "—";
    }

    function feesSummary(pack) {
      const lines = [];
      if (pack.default_fee) {
        const d = pack.default_fee;
        lines.push(`Toutes les villes : ${formatFee(d.delivered_fees)} / ${formatFee(d.refused_fees)} / ${formatFee(d.returned_fees)}`);
      }
      if (pack.cities_count) lines.push(`${pack.cities_count} ville(s) avec un tarif propre`);
      return lines.join("\n");
    }

    // ---------- Grille paginée ----------
    // Seules les lignes modifiées sont gardées (clé : city_id, "all" = Toutes les villes)
    // et envoyées à l'enregistrement : changer de page ou chercher ne perd rien.
    const changes = reactive(new Map());
    const changeCount = computed(() => changes.size);
    const keyOf = (row) => (row.city_id === null ? ALL_CITIES : row.city_id);

    const makeRow = (cityId, cityName, fees) => {
      const row = { city_id: cityId, city_name: cityName, orig: {} };
      FEE_KEYS.forEach((k) => {
        row[k] = isEmptyFee(fees?.[k]) ? "" : Number(fees[k]);
        row.orig[k] = norm(row[k]);
      });
      return row;
    };
    // a pending change wins over the server values
    const withChange = (row) => {
      const change = changes.get(keyOf(row));
      if (change) FEE_KEYS.forEach((k) => (row[k] = change[k] ?? ""));
      return row;
    };

    const defaultRow = reactive(makeRow(null, "Toutes les villes", null));
    const grid = reactive({
      rows: [],
      search: "",
      loading: false,
      meta: { current_page: 1, last_page: 1, from: 0, to: 0, total: 0 },
    });
    let gridRequest = 0;

    async function loadGrid(page = 1) {
      const ticket = ++gridRequest;
      grid.loading = true;
      try {
        const res = await api.get(`/api/auth/pack_city_fees/${formPack.id || 0}`, {
          params: { page, per_page: PER_PAGE, search: grid.search.trim() || undefined },
        });
        if (ticket !== gridRequest) return; // a newer page/search was requested meanwhile
        const d = res.data;
        grid.rows = d.data.map((r) => withChange(makeRow(r.city_id, r.city_name, r.has_fees ? r : null)));
        grid.meta = { current_page: d.current_page, last_page: d.last_page, from: d.from, to: d.to, total: d.total };
        if (page === 1 && !changes.has(ALL_CITIES)) Object.assign(defaultRow, makeRow(null, "Toutes les villes", d.default));
      } catch (error) {
        console.error(error);
        if (ticket === gridRequest) showToast("Impossible de charger les villes.", "error");
      } finally {
        if (ticket === gridRequest) grid.loading = false;
      }
    }

    let searchTimer = null;
    function onGridSearch() {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(() => loadGrid(1), 300);
    }

    const hasValue = (row) => FEE_KEYS.some((k) => !isEmptyFee(row[k]));
    const isChanged = (row) => changes.has(keyOf(row));

    function track(row) {
      const same = FEE_KEYS.every((k) => norm(row[k]) === row.orig[k]);
      if (same) changes.delete(keyOf(row));
      else changes.set(keyOf(row), { city_id: row.city_id, ...Object.fromEntries(FEE_KEYS.map((k) => [k, norm(row[k])])) });
    }

    function clearRow(row) {
      FEE_KEYS.forEach((k) => (row[k] = ""));
      track(row);
    }

    // placeholder = tarif "Toutes les villes" appliqué quand la ville est vide
    const placeholderFor = (key) => (isEmptyFee(defaultRow[key]) ? "—" : String(defaultRow[key]));

    // select options: the default tariff first, then every active city
    const cityOptions = computed(() => [{ id: ALL_CITIES, name: "Toutes les villes (tarif par défaut)" }, ...cities.value]);
    const selectAllCities = () => (feeForm.cities = cities.value.map((c) => c.id));

    function resetFeeForm() {
      feeForm.cities = [];
      FEE_KEYS.forEach((k) => (feeForm[k] = null));
    }

    // Ajout rapide : les mêmes frais pour chaque ville choisie (ou pour le tarif par défaut).
    // Les villes vont directement dans les modifications en attente : pas besoin qu'elles
    // soient sur la page affichée de la grille ; les lignes visibles sont mises à jour.
    function applyFeeForm() {
      if (!feeForm.cities.length) {
        showToast("Choisissez au moins une ville", "error");
        return;
      }
      if (FEE_KEYS.every((k) => isEmptyFee(feeForm[k]))) {
        showToast("Merci de remplir au moins un frais", "error");
        return;
      }
      if (FEE_KEYS.some((k) => !isEmptyFee(feeForm[k]) && Number(feeForm[k]) < 0)) {
        showToast("Les frais doivent être des nombres positifs", "error");
        return;
      }
      const values = Object.fromEntries(FEE_KEYS.map((k) => [k, isEmptyFee(feeForm[k]) ? "" : Number(feeForm[k])]));
      let cityCount = 0;
      let defaultUpdated = false;
      feeForm.cities.forEach((id) => {
        if (id === ALL_CITIES) {
          Object.assign(defaultRow, values);
          track(defaultRow);
          defaultUpdated = true;
          return;
        }
        const visible = grid.rows.find((r) => r.city_id === id);
        if (visible) {
          Object.assign(visible, values);
          track(visible);
        } else {
          changes.set(id, { city_id: id, ...Object.fromEntries(FEE_KEYS.map((k) => [k, norm(values[k])])) });
        }
        cityCount++;
      });
      const parts = [];
      if (cityCount) parts.push(cityCount + " ville" + (cityCount > 1 ? "s" : ""));
      if (defaultUpdated) parts.push("tarif « Toutes les villes »");
      showToast("Frais appliqués : " + parts.join(" + ") + ". Pensez à enregistrer.", "success");
      resetFeeForm();
    }

    // ---------- Modal ----------
    function showModal() {
      const modalEl = document.getElementById("packModal");
      (Modal.getInstance(modalEl) || new Modal(modalEl)).show();
    }

    function hideModal() {
      const modalEl = document.getElementById("packModal");
      (Modal.getInstance(modalEl) || new Modal(modalEl)).hide();
    }

    function openPack(pack) {
      formPack.id = pack ? pack.id : null;
      formPack.name = pack ? pack.name : "";
      changes.clear();
      Object.assign(defaultRow, makeRow(null, "Toutes les villes", null));
      grid.rows = [];
      grid.search = "";
      resetFeeForm();
      showExample.value = false;
      loadGrid(1);
      showModal();
    }

    const openCreate = () => openPack(null);
    function editPack(id) {
      const pack = packs.value.find((p) => p.id === id);
      if (pack) openPack(pack);
    }

    async function submitPack() {
      if (!formPack.name) {
        showToast("Merci de remplir le nom du pack", "error");
        return;
      }
      if (!formPack.id && !changes.size) {
        showToast("Merci d'ajouter au moins un tarif", "error");
        return;
      }
      if (saving.value) return;

      const payload = { name: formPack.name, changes: [...changes.values()] };
      saving.value = true;
      try {
        const response = formPack.id
          ? await api.post(`/api/auth/pack_edit/${formPack.id}`, payload)
          : await api.post("/api/auth/pack_add", payload);

        if (response.data.message === "success") {
          hideModal();
          fetchPacks();
          showToast(formPack.id ? "Pack mis à jour avec succès" : "Pack ajouté avec succès", "success");
        } else {
          showToast("Erreur: " + (response.data.message || response.data), "error");
        }
      } catch (error) {
        console.error(error);
        showToast("Une erreur est survenue lors de l'enregistrement du pack.", "error");
      } finally {
        saving.value = false;
      }
    }

    async function togglePackActive(id, value) {
      const pack = packs.value.find((p) => p.id === id);
      if (pack) {
        pack.active = value;
        await api.post(`/api/auth/active_pack/${id}`, { active: value });
      }
    }

    async function deletePack(id) {
      if (confirm("Voulez-vous vraiment supprimer ce pack ?")) {
        try {
          const response = await api.post(`/api/auth/removePack/${id}`);
          if (response.data.message === "success") {
            fetchPacks();
            showToast("Pack supprimé avec succès", "success");
          } else if (response.data.message === "pack used") {
            showToast(
              "Ce pack est affecté à des clients, vous ne pouvez pas le supprimer",
              "error"
            );
          } else {
            showToast("Erreur lors de la suppression du pack", "error");
          }
        } catch (error) {
          console.error(error);
          showToast("Une erreur est survenue lors de la suppression du pack.", "error");
        }
      }
    }

    return {
      ALL_CITIES,
      cityOptions,
      selectAllCities,
      formPack,
      feeForm,
      feeFields,
      showExample,
      saving,
      cities,
      grid,
      defaultRow,
      changeCount,
      loadGrid,
      onGridSearch,
      hasValue,
      isChanged,
      track,
      clearRow,
      placeholderFor,
      applyFeeForm,
      submitPack,
      openCreate,
      formatFee,
      feesSummary,
      headers,
      packs,
      pagedRows,
      pager,
      goToPage,
      loading,
      themeColor,
      searchTerm,
      togglePackActive,
      editPack,
      deletePack,
      auth,
    };
  },
};
</script>

<style scoped>
/* ---------- Page header ---------- */
.pg-page-icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(6, 180, 138, 0.1);
  color: #06b48a;
  font-size: 20px;
}

/* ---------- Card ---------- */
.pg-card {
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 18px rgba(43, 42, 63, 0.06);
}

.pg-card .card-head {
  border-bottom: 1px solid #f0f0f5;
}

/* ---------- Buttons ---------- */
.pg-btn-primary {
  background: #06b48a;
  padding: 11px 22px;
  border-radius: 8px;
  font-size: 14px;
}

.pg-btn-primary:hover {
  background: #059c78;
  color: #fff;
}

.pg-btn-outline {
  background: transparent;
  border: 1px solid #e2e2ea;
  color: #2b2a3f;
  padding: 10px 22px;
  border-radius: 8px;
  font-size: 14px;
}

.pg-btn-outline:hover {
  background: #f8f8fa;
  border-color: #d5d5e0;
}

/* ---------- Search ---------- */
.pg-search {
  min-width: 260px;
}

.pg-search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #a2a1bb;
  font-size: 14px;
}

.pg-search .form-control {
  border-radius: 8px;
  border: 1px solid #e2e2ea;
  height: 44px;
}

.pg-search .form-control:focus {
  border-color: #06b48a;
  box-shadow: 0 0 0 3px rgba(6, 180, 138, 0.12);
}

/* ---------- Table ---------- */
.pg-table thead th {
  background: #f8f8fb;
  color: #8e8da1;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f1f5;
  white-space: nowrap;
}

.pg-table tbody td {
  padding: 12px 16px;
  font-size: 14px;
  color: #2b2a3f;
  border-bottom: 1px solid #f1f1f5;
  vertical-align: middle;
}

.pg-table tbody tr:nth-child(even) td {
  background: #fbfbfd;
}

.pg-table tbody tr:hover td {
  background: #f8fbfa;
}

/* ---------- Status pills ---------- */
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.status-pill-info {
  background: rgba(31, 177, 230, 0.1);
  color: #1fb1e6;
}

/* ---------- Action icon buttons ---------- */
.pg-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #eceef1;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #8e8da1;
  transition: all 0.2s ease;
}

.pg-icon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pg-icon-btn-edit:hover {
  background: rgba(101, 96, 240, 0.1);
  border-color: transparent;
  color: #6560f0;
}

.pg-icon-btn-delete:hover {
  background: rgba(239, 41, 41, 0.1);
  border-color: transparent;
  color: #ef2929;
}

/* ---------- Modal ---------- */
.pg-modal-content {
  border: none;
  border-radius: 14px;
  overflow: hidden;
}

.pg-modal-header {
  background: #f8f8fb;
  border-bottom: 1px solid #f0f0f5;
  padding: 18px 25px;
}

.pg-modal-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(6, 180, 138, 0.1);
  color: #06b48a;
}

.pg-modal-footer {
  border-top: 1px solid #f0f0f5;
  padding: 16px 25px;
}

.pg-modal-footer .pg-btn-primary,
.pg-modal-footer .pg-btn-outline {
  padding: 9px 20px;
}

.empty-state {
  width: 100%;
}

.status-pill-success {
  background: rgba(6, 180, 138, 0.1);
  color: #06b48a;
  cursor: help;
}

/* ---------- Grille tarifaire par ville ---------- */
.pg-info-pill {
  border: none;
  background: rgba(31, 177, 230, 0.1);
  color: #1fb1e6;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
}

.pg-example {
  background: #f4fbf9;
  border: 1px dashed rgba(6, 180, 138, 0.35);
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #4b4a5e;
}

.pg-fees-table-wrap {
  border: 1px solid #f0f0f5;
  border-radius: 10px;
  max-height: 440px;
  overflow: auto;
  transition: opacity 0.15s ease;
}

.pg-fees-table-wrap.is-loading {
  opacity: 0.55;
}

.pg-fees-table th {
  background: #f8f8fb;
  color: #8e8da1;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 10px 16px;
  position: sticky;
  top: 0;
  z-index: 1;
}

.pg-fees-table td {
  padding: 10px 16px;
  font-size: 14px;
  color: #2b2a3f;
  vertical-align: middle;
  border-color: #f1f1f5;
}

.pg-fees-table tbody tr:last-child td {
  border-bottom: none;
}

.pg-row-editing td {
  background: rgba(6, 180, 138, 0.06);
}

.pg-default-badge {
  background: #f0f0f5;
  color: #8e8da1;
  border-radius: 10px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 500;
}

.pg-inline-input {
  max-width: 160px;
}

.pg-default-row td {
  background: #f8f8fb;
}

/* ville avec un tarif propre / modifiée (non enregistrée) */
.pg-row-own td:first-child {
  box-shadow: inset 3px 0 0 rgba(6, 180, 138, 0.45);
}

.pg-row-changed td {
  background: rgba(6, 180, 138, 0.06);
}

.pg-row-changed td:first-child {
  box-shadow: inset 3px 0 0 #06b48a;
}

.pg-grid-search {
  min-width: 260px;
}

.pg-grid-pager :deep(.pagination-area) {
  margin-top: 12px !important;
}

.pg-changes-note {
  color: #06b48a;
  font-weight: 500;
}

@media (max-width: 575.98px) {
  .pg-grid-search {
    min-width: 0;
    width: 100%;
  }
  .pg-inline-input {
    min-width: 80px;
  }
}

.pg-btn-outline-primary {
  background: transparent;
  border: 1px solid #06b48a;
  color: #06b48a;
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 13px;
}

.pg-btn-outline-primary:hover {
  background: rgba(6, 180, 138, 0.08);
}
</style>
